import Event from './Event'
import track from './track'
import {
  INavigateEvent,
  IFilterEvent,
  ISort,
  IInlineActionEvent,
  IBatchActionEvent,
  ITabEvent,
  IRowExpandEvent,
  ITrackChartClick,
} from './types'

interface BaseEvent<E, P> {
  eventName: E
  props?: P
}

type Navigate = BaseEvent<Event.NAVIGATE, INavigateEvent>
type RowClick = BaseEvent<Event.ROW_CLICK, any>
type RowSelect = BaseEvent<Event.ROW_SELECT, any>
type SelectAll = BaseEvent<Event.SELECT_ALL, any>
type ClearSelection = BaseEvent<Event.CLEAR_SELECTION, any>
type Sort = BaseEvent<Event.SORT, ISort>
type Page = BaseEvent<Event.PAGE, any>
type Print = BaseEvent<Event.PRINT, any>
type Filter = BaseEvent<Event.FILTER, IFilterEvent>
type ClearFilter = BaseEvent<Event.CLEAR_FILTER, IFilterEvent>
type ClearAllFilters = BaseEvent<Event.CLEAR_ALL_FILTERS, any>
type InlineAction = BaseEvent<Event.INLINE_ACTION, IInlineActionEvent>
type BatchAction = BaseEvent<Event.BATCH_ACTION, IBatchActionEvent>
type Import = BaseEvent<Event.IMPORT, any>
type Export = BaseEvent<Event.EXPORT, any>
type NewRecord = BaseEvent<Event.NEW_RECORD_DIALOG, any>
type EditRecord = BaseEvent<Event.EDIT_RECORD_DIALOG, any>
type DeleteRecord = BaseEvent<Event.DELETE_RECORD_DIALOG, any>
type SearchResultClick = BaseEvent<Event.SEARCH_RESULT_CLICK, any>
type TabClick = BaseEvent<Event.TAB_CLICK, ITabEvent>
type RowExpand = BaseEvent<Event.ROW_EXPAND, IRowExpandEvent>
type TokenRegenerate = BaseEvent<Event.TOKEN_REGENERATE, any>
type WorldMapObjectClick = BaseEvent<Event.WORLD_MAP_OBJECT_CLICK, any>
type CalendarDayClick = BaseEvent<Event.CALENDAR_DAY_CLICK, any>
type RemoveRecordAction = BaseEvent<Event.RECORD_REMOVE, any>
type AddRecordAction = BaseEvent<Event.RECORD_ADD, any>
type UndoClick = BaseEvent<Event.UNDO_CLICK, any>
type ViewHistory = BaseEvent<Event.HISTORY, any>
type ChartClick = BaseEvent<Event.CHART_CLICK, ITrackChartClick>
type QR_CODE_SETTINGS_CLICK = BaseEvent<Event.QR_CODE_SETTINGS_CLICK, any>
type DOWNLOAD_QR_CODE = BaseEvent<Event.DOWNLOAD_QR_CODE, any>
export type RootEvent =
  | Navigate
  | RowClick
  | RowSelect
  | SelectAll
  | ClearSelection
  | Sort
  | Page
  | Print
  | Filter
  | ClearFilter
  | ClearAllFilters
  | InlineAction
  | BatchAction
  | Import
  | Export
  | NewRecord
  | EditRecord
  | DeleteRecord
  | SearchResultClick
  | TabClick
  | RowExpand
  | TokenRegenerate
  | WorldMapObjectClick
  | CalendarDayClick
  | RemoveRecordAction
  | AddRecordAction
  | UndoClick
  | ViewHistory
  | ChartClick
  | QR_CODE_SETTINGS_CLICK
  | DOWNLOAD_QR_CODE

function employees({ eventName, props }: RootEvent) {
  const module = 'Employees'
  track(eventName, { module, ...props })
}

function lease({ eventName, props }: RootEvent) {
  const module = 'Leases'
  track(eventName, { module, ...props })
}

function cost({ eventName, props }: RootEvent) {
  const module = 'Cost'
  track(eventName, { module, ...props })
}

function tickets({ eventName, props }: RootEvent) {
  const module = 'Tickets'
  track(eventName, { module, ...props })
}

function ticketDetails({ eventName, props }: RootEvent) {
  const module = 'Ticket Details'
  track(eventName, { module, ...props })
}

function reportCatalog({ eventName, props }: RootEvent) {
  const module = 'Report Catalog'
  track(eventName, { module, ...props })
}

function reportPage({ eventName, reportName, props }: RootEvent & { reportName: string }) {
  const module = 'Report'
  track(eventName, { module, reportName, ...props })
}

function integration({ eventName, props }: RootEvent) {
  const module = 'Integrations'
  track(eventName, { module, ...props })
}

function dashboard({ eventName, props }: RootEvent) {
  const module = 'Dashboard'
  track(eventName, { module, ...props })
}

function workplaceGroups({ eventName, props }: RootEvent) {
  const module = 'Workplace Groups'
  track(eventName, { module, ...props })
}

function workplaceGroupDialog({ eventName, props }: RootEvent) {
  const module = 'Workplace Group Dialog'
  track(eventName, { module, ...props })
}

function buildings({ eventName, props }: RootEvent) {
  const module = 'Building Manager'
  track(eventName, { module, ...props })
}

function portfolio({ eventName, props }: RootEvent) {
  const module = 'Portfolio'
  track(eventName, { module, ...props })
}

function employeeImportDialog({ eventName, props }: RootEvent) {
  const module = 'Employee Import Dialog'
  track(eventName, { module, ...props })
}

function employeeImportHistory({ eventName, props }: RootEvent) {
  const module = 'Employee Import History'
  track(eventName, { module, ...props })
}

function assets({ eventName, props }: RootEvent) {
  const module = 'Assets'
  track(eventName, { module, ...props })
}

function assetDialog({ eventName, props }: RootEvent) {
  const module = 'Asset Dialog'
  track(eventName, { module, ...props })
}

function neighborhoods({ eventName, props }: RootEvent) {
  const module = 'Neighborhoods'
  track(eventName, { module, ...props })
}

function neighborhoodDialog({ eventName, props }: RootEvent) {
  const module = 'Neighborhood Dialog'
  track(eventName, { module, ...props })
}

function neighborhoodImportDialog({ eventName, props }: RootEvent) {
  const module = 'Neighborhood Import Dialog'
  track(eventName, { module, ...props })
}

function neighborhoodImportHistory({ eventName, props }: RootEvent) {
  const module = 'Neighborhood Import History'
  track(eventName, { module, ...props })
}

function departments({ eventName, props }: RootEvent) {
  const module = 'Departments'
  track(eventName, { module, ...props })
}

function departmentDialog({ eventName, props }: RootEvent) {
  const module = 'Department Dialog'
  track(eventName, { module, ...props })
}

function importDialog({ module, eventName, props }: RootEvent & { module: string }) {
  track(eventName, { module, ...props })
}

function bookings({ eventName, props }: RootEvent) {
  const module = 'Bookings'
  track(eventName, { module, ...props })
}

function bookingImportHistory({ eventName, props }: RootEvent) {
  const module = 'Bookings History'
  track(eventName, { module, ...props })
}

function bookingImportDialog({ eventName, props }: RootEvent) {
  const module = 'Bookings Import Dialog'
  track(eventName, { module, ...props })
}

function buildingTypes({ eventName, props }: RootEvent) {
  const module = 'Building Types'
  track(eventName, { module, ...props })
}

function buildingTypeDialog({ eventName, props }: RootEvent) {
  const module = 'Building Type Dialog'
  track(eventName, { module, ...props })
}

function projections({ eventName, props }: RootEvent) {
  const module = 'Workplace Groups'
  track(eventName, { module, ...props })
}

function spaceTypes({ eventName, props }: RootEvent) {
  const page = 'Space Types'
  track(eventName, { page, ...props })
}

function spaceTypeDialog({ eventName, props }: RootEvent) {
  const page = 'Space Type Dialog'
  track(eventName, { page, ...props })
}
function qrCodeSetting({ eventName, props }: RootEvent) {
  const page = 'Settings'
  track(eventName, { page, ...props })
}
function downloadQRCode({ eventName, props }: RootEvent) {
  const page = 'Buildings/Floors'
  track(eventName, { page, ...props })
}

export default {
  Event,
  employees,
  tickets,
  ticketDetails,
  cost,
  lease,
  reportCatalog,
  reportPage,
  integration,
  dashboard,
  workplaceGroups,
  workplaceGroupDialog,
  buildings,
  portfolio,
  employeeImportDialog,
  employeeImportHistory,
  assets,
  assetDialog,
  neighborhoods,
  neighborhoodDialog,
  neighborhoodImportDialog,
  neighborhoodImportHistory,
  departmentDialog,
  departments,
  importDialog,
  bookings,
  bookingImportDialog,
  bookingImportHistory,
  buildingTypes,
  buildingTypeDialog,
  spaceTypes,
  spaceTypeDialog,
  projections,
  qrCodeSetting,
  downloadQRCode,
}
